/**
 * Page Size Select
 *
 * "Rows per page" control shared by every paginated transaction list (Home,
 * /transactions, Scenario Editor) so the choice of page size — and its
 * label/options — stays consistent across all three.
 *
 * @fileoverview Reusable page-size control for paginated tables
 */

import React from 'react';
import { HStack, Text, Select } from '@chakra-ui/react';

export const PAGE_SIZE_OPTIONS = [25, 50, 100, 200] as const;

export interface PageSizeSelectProps {
  pageSize: number;
  onChange: (size: number) => void;
  options?: readonly number[];
  isDisabled?: boolean;
}

export const PageSizeSelect: React.FC<PageSizeSelectProps> = ({
  pageSize,
  onChange,
  options = PAGE_SIZE_OPTIONS,
  isDisabled = false,
}) => {
  // If the current pageSize isn't one of the offered options (e.g. a
  // caller's own default doesn't match PAGE_SIZE_OPTIONS), still show it as
  // a selectable value rather than silently rendering an option list that
  // doesn't match the control's actual value.
  const allOptions = options.includes(pageSize)
    ? options
    : [...options, pageSize].sort((a, b) => a - b);

  return (
    <HStack spacing={2}>
      <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">
        Rows per page
      </Text>
      <Select
        size="sm"
        width="80px"
        value={pageSize}
        onChange={(e) => onChange(Number(e.target.value))}
        isDisabled={isDisabled}
        aria-label="Rows per page"
      >
        {allOptions.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </Select>
    </HStack>
  );
};

export default PageSizeSelect;
